import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2,
  Loader2,
  Lock,
  Send,
  Sparkles,
  Wallet,
  Calendar as CalendarIcon,
  User,
  ShieldCheck,
  KeyRound,
  LogOut,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DateInput } from "@/components/ui/date-input";
import { cn } from "@/lib/utils";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API_URL });

const FIELD_LABELS = {
  cash: "Készpénz",
  card: "Kártya",
  wolt: "Wolt",
  foodora: "Foodora",
  kiosk: "Kiosk",
  app: "App",
};

const fmtFt = (n) =>
  new Intl.NumberFormat("hu-HU").format(Math.round(n || 0)) + " Ft";
const fmtThousands = (n) => new Intl.NumberFormat("hu-HU").format(Math.round(n || 0));

// Csak számjegyeket tartunk meg + maximum egy értelmes számértékké alakítunk.
const parseDigits = (s) => {
  const digits = String(s || "").replace(/[^\d]/g, "");
  return digits;
};

export default function PublicDailyRevenuePage() {
  const { slug } = useParams();
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // POS auth state
  const [posId, setPosId] = useState("");
  const [posPassword, setPosPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [authing, setAuthing] = useState(false);
  const [authedEmployee, setAuthedEmployee] = useState(null); // {employee_name, position}

  const [date, setDate] = useState("");
  const [amounts, setAmounts] = useState({}); // raw digit-only strings
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/daily-revenues/${slug}`);
        setTpl(data);
        setDate(data.suggested_date);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const fields = useMemo(
    () => Object.entries(tpl?.fields || {}).filter(([_, v]) => v).map(([k]) => k),
    [tpl],
  );
  const total = useMemo(
    () => fields.reduce((s, f) => s + (Number(amounts[f]) || 0), 0),
    [fields, amounts],
  );

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-gradient-to-br from-slate-50 to-indigo-50/40 dark:from-background dark:to-indigo-500/[0.06]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (notFound || !tpl) {
    return (
      <div className="grid min-h-screen place-items-center bg-gradient-to-br from-rose-50 to-slate-50 p-6 dark:from-rose-500/[0.08] dark:to-background">
        <div className="max-w-md rounded-3xl border border-border bg-card p-7 text-center shadow-[0_30px_80px_-40px_rgba(15,23,42,0.25)]">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-rose-500/15 text-rose-700 dark:text-rose-300">
            <Lock className="h-6 w-6" />
          </div>
          <h1 className="mt-4 text-lg font-semibold tracking-tight">
            Az űrlap nem elérhető
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            A link érvénytelen vagy az űrlap inaktiválva lett. Kérd el az
            aktuálisat a vezetődtől.
          </p>
        </div>
      </div>
    );
  }

  const canSubmit = !!authedEmployee && total > 0;

  const onPosLogin = async (e) => {
    e?.preventDefault?.();
    const pid = posId.trim();
    const pwd = posPassword.trim();
    if (!pid || !pwd) {
      toast.error("Add meg a POS ID-t és a jelszót.");
      return;
    }
    setAuthing(true);
    try {
      const { data } = await publicApi.post(
        `/public/daily-revenues/${slug}/pos-login`,
        { pos_id: pid, pos_password: pwd },
      );
      setAuthedEmployee(data);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Hibás POS ID vagy jelszó.",
      );
    } finally {
      setAuthing(false);
    }
  };

  const onLogout = () => {
    setAuthedEmployee(null);
    setPosPassword("");
    setAmounts({});
    setNotes("");
    setDone(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!authedEmployee) {
      toast.error("Először jelentkezz be a POS adataiddal.");
      return;
    }
    if (total <= 0) {
      toast.error("Legalább egy mezőhöz adj meg pozitív összeget.");
      return;
    }
    const cleanAmounts = {};
    fields.forEach((f) => {
      const v = Number(amounts[f]) || 0;
      if (v > 0) cleanAmounts[f] = v;
    });
    setSubmitting(true);
    try {
      const { data } = await publicApi.post(`/public/daily-revenues/${slug}`, {
        date,
        amounts: cleanAmounts,
        pos_id: posId.trim(),
        pos_password: posPassword.trim(),
        notes: notes.trim() || undefined,
      });
      setDone(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Hiba történt a rögzítéskor.";
      toast.error(msg);
      // Ha a POS session érvénytelen lett, kérjük újra a bejelentkezést
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setAuthedEmployee(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 px-4 py-10 dark:from-emerald-500/[0.08] dark:via-background dark:to-emerald-500/[0.04]">
        <div className="mx-auto max-w-md rounded-3xl border border-emerald-300/60 bg-card/95 p-7 text-center shadow-[0_30px_80px_-30px_rgba(16,185,129,0.4)] backdrop-blur">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-[0_12px_30px_-12px_rgba(16,185,129,0.65)]">
            <CheckCircle2 className="h-7 w-7" strokeWidth={2} />
          </div>
          <h1 className="mt-4 text-xl font-semibold tracking-tight">
            Köszönjük, sikeresen rögzítve!
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Összesen{" "}
            <b className="tabular-nums text-foreground">{fmtFt(done.total)}</b>{" "}
            került feltöltésre. Az ellenőrzést a vezető fogja elvégezni.
          </p>
          <Button
            className="mt-6 h-11 w-full"
            variant="outline"
            onClick={() => {
              setDone(null);
              setAmounts({});
              setNotes("");
            }}
            data-testid="submit-another"
          >
            Új rögzítés
          </Button>
        </div>
      </div>
    );
  }

  if (!authedEmployee) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 to-indigo-50/30 pb-20 dark:from-background dark:to-indigo-500/[0.06]">
        <div className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-indigo-400/15 blur-3xl" />
        <div className="pointer-events-none absolute -right-16 top-40 h-64 w-64 rounded-full bg-emerald-400/15 blur-3xl" />

        <header className="relative overflow-hidden bg-gradient-to-br from-foreground to-indigo-900 px-5 pb-10 pt-7 text-background dark:from-slate-900 dark:to-indigo-950">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.08] [background-image:radial-gradient(circle_at_1px_1px,_white_1px,_transparent_0)] [background-size:18px_18px]"
            aria-hidden
          />
          <div className="relative mx-auto max-w-lg">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.24em] text-background/70">
              <Sparkles className="h-3 w-3" />
              Napi bevétel rögzítés
            </div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight">
              {tpl.site_name}
            </h1>
            {tpl.company_name && (
              <p className="mt-1 text-sm text-background/70">
                {tpl.company_name}
              </p>
            )}
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-background/15 bg-background/10 px-2.5 py-1 text-[11px] font-medium">
              <ShieldCheck className="h-3 w-3" /> POS azonosítás szükséges
            </div>
          </div>
        </header>

        <form
          onSubmit={onPosLogin}
          className="relative mx-auto -mt-5 max-w-lg space-y-4 px-4"
          data-testid="pos-login-form"
        >
          <section className="rounded-2xl border border-border bg-card p-5 shadow-[0_18px_40px_-20px_rgba(15,23,42,0.18)]">
            <div className="flex items-center gap-2.5">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white shadow-[0_10px_24px_-10px_rgba(79,70,229,0.55)]">
                <KeyRound className="h-4 w-4" />
              </span>
              <div>
                <h2 className="text-base font-semibold tracking-tight">
                  Bejelentkezés
                </h2>
                <p className="text-[12px] text-muted-foreground">
                  Add meg a POS ID-d és a jelszavad a bevétel rögzítéséhez.
                </p>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <div>
                <Label htmlFor="pos-id" className="text-[12px]">
                  POS ID <span className="text-rose-500">*</span>
                </Label>
                <Input
                  id="pos-id"
                  inputMode="numeric"
                  autoComplete="username"
                  placeholder="Pl. 12"
                  value={posId}
                  onChange={(e) => setPosId(e.target.value)}
                  className="h-11 text-base"
                  data-testid="pos-id-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="pos-pwd" className="text-[12px]">
                  POS jelszó <span className="text-rose-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="pos-pwd"
                    type={showPwd ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••"
                    value={posPassword}
                    onChange={(e) => setPosPassword(e.target.value)}
                    className="h-11 pr-10 text-base"
                    data-testid="pos-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
                    aria-label={showPwd ? "Jelszó elrejtése" : "Jelszó megjelenítése"}
                    data-testid="toggle-pos-pwd"
                  >
                    {showPwd ? (
                      <EyeOff className="h-4 w-4" strokeWidth={1.75} />
                    ) : (
                      <Eye className="h-4 w-4" strokeWidth={1.75} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <Button
            type="submit"
            disabled={authing}
            className="h-12 w-full bg-gradient-to-br from-indigo-600 to-indigo-700 text-base text-white shadow-[0_12px_30px_-12px_rgba(79,70,229,0.5)] hover:from-indigo-700 hover:to-indigo-800"
            data-testid="pos-login-submit"
          >
            {authing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <KeyRound className="h-4 w-4" />
            )}
            Belépés
          </Button>

          <p className="px-2 text-center text-[11px] text-muted-foreground">
            <ShieldCheck className="mr-1 inline h-3 w-3 text-emerald-600" />
            Csak az adott telephelyen dolgozó kollégák léphetnek be.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 to-indigo-50/30 pb-20 dark:from-background dark:to-indigo-500/[0.06]">
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-indigo-400/15 blur-3xl" />
      <div className="pointer-events-none absolute -right-16 top-40 h-64 w-64 rounded-full bg-emerald-400/15 blur-3xl" />

      {/* Hero header */}
      <header className="relative overflow-hidden bg-gradient-to-br from-foreground to-indigo-900 px-5 pb-10 pt-7 text-background dark:from-slate-900 dark:to-indigo-950">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.08] [background-image:radial-gradient(circle_at_1px_1px,_white_1px,_transparent_0)] [background-size:18px_18px]"
          aria-hidden
        />
        <div className="relative mx-auto max-w-lg">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.24em] text-background/70">
                <Sparkles className="h-3 w-3" />
                Napi bevétel rögzítés
              </div>
              <h1 className="mt-2 text-2xl font-semibold tracking-tight">
                {tpl.site_name}
              </h1>
              {tpl.company_name && (
                <p className="mt-1 text-sm text-background/70">
                  {tpl.company_name}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-background/20 bg-background/10 px-3 py-1.5 text-[11px] font-medium text-background/90 transition-colors hover:bg-background/20"
              data-testid="pos-logout"
            >
              <LogOut className="h-3 w-3" />
              Kilépés
            </button>
          </div>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-background/15 bg-background/10 px-2.5 py-1 text-[11px] font-medium">
            <ShieldCheck className="h-3 w-3" /> Csak a vezetőd látja
          </div>
        </div>
      </header>

      <form
        onSubmit={onSubmit}
        className="relative mx-auto -mt-5 max-w-lg space-y-4 px-4"
        data-testid="public-revenue-form"
      >
        {/* Identity + date */}
        <section className="rounded-2xl border border-border bg-card p-5 shadow-[0_18px_40px_-20px_rgba(15,23,42,0.18)]">
          <SectionLabel icon={User}>Ki és mikor</SectionLabel>
          <div className="mt-3 space-y-3">
            <div
              className="flex items-center gap-3 rounded-xl border border-emerald-300/50 bg-emerald-50/50 px-3 py-2.5 dark:bg-emerald-500/[0.06]"
              data-testid="public-authed-employee"
            >
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-[0_8px_18px_-8px_rgba(16,185,129,0.55)]">
                <ShieldCheck className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700/80 dark:text-emerald-300/80">
                  Bejelentkezve
                </p>
                <p className="truncate text-sm font-semibold tracking-tight">
                  {authedEmployee.employee_name}
                </p>
                {authedEmployee.position && (
                  <p className="truncate text-[11px] text-muted-foreground">
                    {authedEmployee.position}
                  </p>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="dt" className="text-[12px]">
                <CalendarIcon className="mr-1 inline h-3 w-3 text-muted-foreground" />
                Dátum
              </Label>
              <Input
                id="dt"
                type="hidden"
                value={date}
                readOnly
                data-testid="public-date"
              />
              <DateInput
                value={date}
                onChange={setDate}
                clearable={false}
                className="h-11 text-base"
              />
            </div>
          </div>
        </section>

        {/* Amounts */}
        <section className="rounded-2xl border border-border bg-card p-5 shadow-[0_18px_40px_-20px_rgba(15,23,42,0.18)]">
          <SectionLabel icon={Wallet}>Bevétel-mezők</SectionLabel>
          <div className="mt-3 space-y-3">
            {fields.map((f) => {
              const raw = amounts[f] || "";
              const num = Number(raw) || 0;
              const display = raw ? `${fmtThousands(num)} Ft` : "";
              return (
                <div key={f} className="space-y-1">
                  <Label htmlFor={`am-${f}`} className="text-[12px]">
                    {FIELD_LABELS[f] || f}
                  </Label>
                  <div className="relative">
                    <Input
                      id={`am-${f}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="0 Ft"
                      value={display}
                      onChange={(e) =>
                        setAmounts((p) => ({
                          ...p,
                          [f]: parseDigits(e.target.value),
                        }))
                      }
                      className={cn(
                        "h-12 pr-3 text-right text-lg font-semibold tabular-nums",
                        num > 0 && "border-emerald-300 bg-emerald-50/40 dark:bg-emerald-500/[0.05]",
                      )}
                      data-testid={`amount-${f}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {/* Live total */}
          <div className="mt-4 flex items-center justify-between rounded-2xl border border-foreground/[0.08] bg-gradient-to-r from-foreground/[0.025] to-transparent px-4 py-3.5">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              <Sparkles className="h-3 w-3" /> Összesen
            </span>
            <span
              className={cn(
                "text-xl font-bold tabular-nums tracking-tight",
                total > 0 ? "text-foreground" : "text-muted-foreground/40",
              )}
              data-testid="public-total"
            >
              {fmtFt(total)}
            </span>
          </div>
        </section>

        {/* Notes */}
        <section className="rounded-2xl border border-border bg-card p-5 shadow-[0_18px_40px_-20px_rgba(15,23,42,0.18)]">
          <SectionLabel>Megjegyzés (opcionális)</SectionLabel>
          <Textarea
            id="nt"
            placeholder="Bármi, amit a vezetőnek jelezni szeretnél…"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-3 text-base"
            data-testid="public-notes"
          />
        </section>

        <Button
          type="submit"
          disabled={submitting || !canSubmit}
          className="h-12 w-full bg-gradient-to-br from-indigo-600 to-indigo-700 text-base text-white shadow-[0_12px_30px_-12px_rgba(79,70,229,0.5)] hover:from-indigo-700 hover:to-indigo-800"
          data-testid="public-submit"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Rögzítés elküldése
        </Button>

        <p className="px-2 text-center text-[11px] text-muted-foreground">
          <ShieldCheck className="mr-1 inline h-3 w-3 text-emerald-600" />
          A leadott összegeket a vezető fogja ellenőrizni a borítékkal együtt.
        </p>
      </form>
    </div>
  );
}

function SectionLabel({ icon: Icon, children }) {
  return (
    <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
      {Icon && (
        <span className="grid h-5 w-5 place-items-center rounded-md bg-foreground/[0.06]">
          <Icon className="h-3 w-3" strokeWidth={2} />
        </span>
      )}
      {children}
    </p>
  );
}
